import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { AuthError, authService } from '@/services/auth.service';
import type { LoginCredentials, User } from '@/types';
import { mapApiUserToUser } from '@/types';

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  clearError: () => void;
  clearSession: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,

      // Login action
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login(credentials);
          const user = mapApiUserToUser(response.user);

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          });
        } catch (error) {
          const message =
            error instanceof AuthError ? error.message : 'An unexpected error occurred';

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });

          throw error;
        }
      },

      // Logout action
      logout: async () => {
        set({ isLoading: true });

        try {
          await authService.logout();
        } finally {
          // Always clear local state
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      // Check session validity (called on app mount)
      checkSession: async () => {
        // Skip if already initialized and not authenticated
        const state = get();
        if (state.isInitialized && !state.isAuthenticated) {
          return;
        }

        set({ isLoading: true });

        try {
          const response = await authService.getCurrentUser();
          const user = mapApiUserToUser(response.user);

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          });
        } catch {
          // Session invalid or expired
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          });
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Clear session (called by API client on 401)
      clearSession: () =>
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        }),
    }),
    {
      name: 'admin-auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields (not loading states)
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
